import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Container } from 'rsuite';
import {
  BigNumbersArea,
  CoberturaProdutosRedeChart,
  CoberturaTotal,
  HistoricoCoberturaChart,
  HeaderAnaliseCobertura,
  EmptyState,
  ProductsCoverageChart,
} from './components';
import { useAnaliseCoberturaStore } from './AnaliseCobertura.store';
import { useAnaliseCoberturaBigNumbers } from './hooks/useAnaliseCoberturaBigNumbers';
import { promoDueDateFormat } from '../../../IPA/PlataformaPrecificacao/utils';
import { useAnaliseCoberturaTimeline } from './hooks/useAnaliseCoberturaTimeline';
import { useAnaliseCoberturaRede } from './hooks/useAnaliseCoberturaRede';
import * as service from './services';
import styles from './AnaliseCobertura.module.scss';
import { useCoverageAnalysisSection } from './hooks/useCoverageAnalysisSection';
import { useCoverageAnalysisUf } from './hooks/useCoverageAnalysisUf';
import { useCoverageAnalysisCategory } from './hooks/useCoverageAnalysisCategory';
import { useCoverageAnalysisCity } from './hooks/useCoverageAnalysisCity';
import { LoadingSpinerArea } from '../../../../components';

export const AnaliseCobertura = () => {
  const { clienteData } = useSelector(
    (state: any) => ({
      clienteData: state.clienteDataReducer?.clienteData,
    }),
    shallowEqual,
  );

  const {
    bigNumbersData,
    coberturaRedeData,
    sectionCoverageData,
    sectionCoverageParams,
    ufCoverageData,
    ufCoverageParams,
    categoryCoverageData,
    categoryCoverageParams,
    cityCoverageData,
  } = useAnaliseCoberturaStore();

  const companyName = clienteData?.contrato?.cliente?.organizacao?.nome;

  const { coberturaRedeLoading, handleChangeCoberturaRedePagination, handleChangeCoberturaRedePeriod, handleChangeCoberturaRedeSearch } =
    useAnaliseCoberturaRede();

  const {
    SectionCoverageLoading,
    handleRequestSectionCoverageTableList,
    handleChangeSectionCoveragePagination,
    handleChangeSectionCoveragePeriod,
    handleChangeSectionCoverageSearch,
    handleSelectSectionCoverageFilter,
    handleCleanSectionCoverageFilter,
  } = useCoverageAnalysisSection();

  const {
    UfCoverageLoading,
    handleRequestUfCoverageTableList,
    handleChangeUfCoveragePagination,
    handleChangeUfCoveragePeriod,
    handleChangeUfCoverageSearch,
    handleSelectUfCoverageFilter,
    handleCleanUfCoverageFilter,
  } = useCoverageAnalysisUf();

  const {
    CategoryCoverageLoading,
    handleRequestCategoryCoverageTableList,
    handleChangeCategoryCoveragePagination,
    handleChangeCategoryCoveragePeriod,
    handleChangeCategoryCoverageSearch,
    handleSelectCategoryCoverageFilter,
    handleCleanCategoryCoverageFilter,
  } = useCoverageAnalysisCategory();

  const { cityCoverageLoading, handleChangeCityCoveragePagination, handleChangeCityCoverageSearch } = useCoverageAnalysisCity();

  const { handleChangeBigNumbersPeriod, isLoading, coberturaStatus } = useAnaliseCoberturaBigNumbers();
  const { isLoadingTimeline, timelineData } = useAnaliseCoberturaTimeline();

  const handleChangeTimelinePeriod = (value: number) => {
    handleChangeBigNumbersPeriod(value);
    handleChangeCoberturaRedePeriod(value);
    handleChangeSectionCoveragePeriod(value);
    handleChangeUfCoveragePeriod(value);
    handleChangeCategoryCoveragePeriod(value);
  };

  const handleHistoricoCoberturaDownload = async () => {
    await service.downloadHistoricoCobertura();
  };

  const handleHistoricoCoberturaOptions = (value: number) => {
    if (value === 1) {
      handleHistoricoCoberturaDownload();
    }
  };

  const handleDownloadProductsNotFound = async () => {
    await service.downloadProductNotFound({ numeroDias: bigNumbersData.janelaEmDias, dataFinal: bigNumbersData.dataFinal });
  };

  return (
    <Container className={['main-container', styles['analise-cobertura-container']].join(' ')}>
      <div id="container">
        <LoadingSpinerArea area="analise-cobertura" />
        <HeaderAnaliseCobertura status={coberturaStatus} date={promoDueDateFormat(bigNumbersData?.dataCalculo)} onChangePeriod={handleChangeTimelinePeriod} />
        {coberturaStatus !== 200 && coberturaStatus !== 422 ? <EmptyState type="PENDING" /> : null}
        {coberturaStatus === 422 ? <EmptyState type="NO_DATA" /> : null}
        {coberturaStatus === 200 ? (
          <div>
            <BigNumbersArea data={bigNumbersData} isLoading={isLoading} onClickDownload={handleDownloadProductsNotFound} />
            <div className={styles['cobertura-total-wrapper']}>
              <div className={styles['cobertura-total-inner-wrapper']}>
                <div className={styles['company-name']}>{companyName}</div>
                <CoberturaTotal data={bigNumbersData} isLoading={isLoading} />
              </div>
              <HistoricoCoberturaChart data={timelineData} isLoading={isLoadingTimeline} onSelectThreeDotButtonOption={handleHistoricoCoberturaOptions} />
            </div>
            <CoberturaProdutosRedeChart
              data={coberturaRedeData}
              loading={coberturaRedeLoading}
              onChangePage={handleChangeCoberturaRedePagination}
              onSearch={handleChangeCoberturaRedeSearch}
            />
            <div className={styles['coverage-products-wrapper']}>
              <ProductsCoverageChart
                name="uf"
                data={ufCoverageData}
                loading={UfCoverageLoading}
                onSelect={(_v, item) => handleSelectUfCoverageFilter(item)}
                onSearch={handleChangeUfCoverageSearch}
                onOpen={() => handleChangeUfCoverageSearch('')}
                onClean={handleCleanUfCoverageFilter}
                onClose={() => handleRequestUfCoverageTableList(ufCoverageParams)}
                onChangePage={handleChangeUfCoveragePagination}
              />
              <ProductsCoverageChart
                name="city"
                data={cityCoverageData}
                loading={cityCoverageLoading}
                onSearch={handleChangeCityCoverageSearch}
                onChangePage={handleChangeCityCoveragePagination}
              />
            </div>
            <div className={styles['coverage-products-wrapper']}>
              <ProductsCoverageChart
                name="section"
                data={sectionCoverageData}
                loading={SectionCoverageLoading}
                onSelect={(_v, item) => handleSelectSectionCoverageFilter(item)}
                onSearch={handleChangeSectionCoverageSearch}
                onOpen={() => handleChangeSectionCoverageSearch('')}
                onClean={handleCleanSectionCoverageFilter}
                onClose={() => handleRequestSectionCoverageTableList(sectionCoverageParams)}
                onChangePage={handleChangeSectionCoveragePagination}
              />
              <ProductsCoverageChart
                name="category"
                data={categoryCoverageData}
                loading={CategoryCoverageLoading}
                onSelect={(_v, item) => handleSelectCategoryCoverageFilter(item)}
                onSearch={handleChangeCategoryCoverageSearch}
                onOpen={() => handleChangeCategoryCoverageSearch('')}
                onClean={handleCleanCategoryCoverageFilter}
                onClose={() => handleRequestCategoryCoverageTableList(categoryCoverageParams)}
                onChangePage={handleChangeCategoryCoveragePagination}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Container>
  );
};
